import React, { useState, useRef } from 'react'
import * as alertStyles from '../SharedCss/alert.css'
import * as formStyles from '../SharedCss/form.css'
import { getAuthenticatedUser, getAuthenticatedUserData, fetchUserData } from '../Auth'
import MessageBus from '../MessageBus'
import Menu from '../Menu'
import Logger from '../Logger'
import Footer from '../SharedComponents/Footer'
import ALERT_TYPES from '../SharedConstants/AlertTypes'
import LoadingBar from 'react-top-loading-bar'
import { formatPhoneNumber } from './helpers'
require('dotenv').config()

export default function MfaSettings (props) {
  const { CognitoIdentityLib } = props
  const cognitoUser = getAuthenticatedUser()
  const userData = getAuthenticatedUserData()
  const currentPhoneNumber = userData.getAttr('phone_number') || ''
  const currentMfaEnabled = userData.isSmsMfaActivated()
  const currentMfaDelivery = userData.getAttr('custom:mfa_delivery') || 'sms'

  const [mfaEnabled, setMfaEnabled] = useState(currentMfaEnabled)
  const [phone, setPhone] = useState(currentPhoneNumber)
  const [mfaDelivery, setMfaDelivery] = useState(currentMfaDelivery)
  const [alertType, setAlertType] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const loadingRef = useRef(null)

  const updateUserAttributes = (includeDeliveryPreference = false) => {
    return new Promise((resolve, reject) => {
      const formattedPhone = formatPhoneNumber(phone)
      const attributes = [{
        Name: 'phone_number',
        Value: formattedPhone
      }]

      if (includeDeliveryPreference) {
        attributes.push({
          Name: 'custom:mfa_delivery',
          Value: mfaDelivery
        })
      }

      Logger.start('Update User Attributes', attributes)

      const attributeList = attributes.map(attr =>
        new CognitoIdentityLib.CognitoUserAttribute(attr)
      )

      cognitoUser.updateAttributes(attributeList, function (err, result) {
        if (err) {
          reject(err)
        }

        Logger.done('Update User Attributes', attributes)
        setPhone(formattedPhone)
        resolve('done')
      })
    })
  }

  const updateUserMfa = () => {
    return new Promise((resolve, reject) => {
      let settings = {}

      if (mfaEnabled) {
        settings = {
          PreferredMfa: true,
          Enabled: true
        }
      } else {
        settings = {
          Enabled: false
        }
      }

      Logger.start('Update User MFA', settings)

      cognitoUser.setUserMfaPreference(settings, null, (err, result) => {
        if (err) {
          reject(err)
        }

        Logger.done('Update User MFA', settings)
        setAlertType(ALERT_TYPES.UPDATE_SUCCESSFULLY)
        resolve('done')
      })
    })
  }

  const handleSubmit = async (e) => {
    setAlertType('')
    e.preventDefault()

    const hasPhoneChange = phone !== currentPhoneNumber
    const hasMfaChange = mfaEnabled !== currentMfaEnabled
    const hasDeliveryChange = mfaDelivery !== currentMfaDelivery

    if (!hasPhoneChange && !hasMfaChange && !hasDeliveryChange) {
      return
    }

    loadingRef.current.continuousStart()

    if (phone.trim().length === 0) {
      setAlertType(ALERT_TYPES.UPDATE_FAILED)
      setErrorMessage('Phone number is required')
      loadingRef.current.complete()
      return
    }

    try {
      if (hasPhoneChange || hasDeliveryChange) {
        await updateUserAttributes(true)
      } else if (hasDeliveryChange && !hasPhoneChange) {
        await updateUserAttributes(true)
      }

      if (hasMfaChange) {
        await updateUserMfa()
      }

      loadingRef.current.complete()
      emitUserAttrsChange()
      fetchUserData()
      setAlertType(ALERT_TYPES.UPDATE_SUCCESSFULLY)
    } catch (err) {
      loadingRef.current.complete()
      setAlertType(ALERT_TYPES.UPDATE_FAILED)
      setErrorMessage(err.message || JSON.stringify(err))
    }
  }

  const emitUserAttrsChange = () => {
    MessageBus.emitUserAttrsChange({
      username: userData.getAttr('sub'),
      bl_id: userData.getAttr('custom:bl_id')
    })
  }

  const renderAlert = () => {
    if (alertType === ALERT_TYPES.UPDATE_FAILED) {
      return (
        <div className={[alertStyles.alert, alertStyles.error, alertStyles.render_with_menu].join(' ')}>
          { errorMessage }
        </div>
      )
    } else if (alertType === ALERT_TYPES.UPDATE_SUCCESSFULLY) {
      return (
        <div className={[alertStyles.alert, alertStyles.success, alertStyles.render_with_menu].join(' ')}>
          Update settings successfully.
        </div>
      )
    }
  }

  return <div className={formStyles.container}>
    { renderAlert() }
    <LoadingBar color='#f11946' ref={loadingRef} />

    <Menu highlighted="mfa_settings" />
    <div>
      <div className={formStyles.header}>
        <div className={formStyles.logo}>
          <h1>Blueleaf</h1>
        </div>

        <div className={formStyles.header_text}>
          <h2>Update your MFA settings</h2>
        </div>
      </div>

      <div className={formStyles.form_container}>
        <form id="new-password-form" onSubmit={handleSubmit}>
          <div className={formStyles.fieldset}>
            <label className={formStyles.label}>
              Phone Number (Required)
            </label>
            <input className={formStyles.input_field} id="phone-number" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </div>

          <div className={formStyles.fieldset}>
            <label className={formStyles.label}>
              Activate MFA
              <input className={formStyles.checkbox} type="checkbox" checked={mfaEnabled} onChange={(e) => setMfaEnabled(e.target.checked)} />
            </label>
          </div>

          <div
            className={formStyles.fieldset}
            style={{
              maxHeight: mfaEnabled ? '100px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out',
              opacity: mfaEnabled ? '1' : '0',
              paddingTop: mfaEnabled ? '15px' : '0',
              paddingBottom: mfaEnabled ? '15px' : '0'
            }}
          >
            <label className={formStyles.label}>
              MFA Delivery Method
            </label>
            <div>
              <label style={{ marginRight: '20px' }}>
                <input
                  type="radio"
                  name="mfa_delivery"
                  value="sms"
                  checked={mfaDelivery === 'sms'}
                  onChange={(e) => setMfaDelivery(e.target.value)}
                  style={{ marginRight: '5px' }}
                />
                SMS
              </label>
              <label>
                <input
                  type="radio"
                  name="mfa_delivery"
                  value="email"
                  checked={mfaDelivery === 'email'}
                  onChange={(e) => setMfaDelivery(e.target.value)}
                  style={{ marginRight: '5px' }}
                />
                Email
              </label>
            </div>
          </div>

          <input id="submit-btn" className={formStyles.submit_btn} type="submit" value="Set" />
        </form>
      </div>

      <Footer />
    </div>
  </div>
}
